/**
 * Legacy Sendbird v3 event watchers saga.
 *
 * Restored from git (c5c927eeb^). Import paths updated to reference
 * `@/domain/chat/legacy/` for v3-only code.
 */
import { call, put, take, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { sbChannelList } from 'sendbird-utils';
import { channelsToThreads } from 'sendbird-utils/lib/imAdapters';
import CONSTANTS from '@/domain/constants';
import * as Chat from '@/domain/chat/legacy/sagas';
import * as LegacyActions from '@/domain/chat/legacy/chatActions';
import { signOutAction } from '@/domain/env';
import { getOrganizationFeatures } from '@/domain/organization/organizationActions';
import { getRuntimeConfig } from '@/lib/runtimeConfig';

import { notify } from '@/lib/errorLogger';
import { messagesLoadedSelector, isNewChatSelector } from '@/domain/chat/chatSelector';
import { allowedChatWorkerSelector } from '@/domain/chat/legacy/chatSelector';
import { ensureAllChatChannel } from '@/domain/chat/legacy/sagas';
import { setOpenAction, setActiveChannelAction } from '@/domain/chat/chatActions';
import { MultyWorker } from '@/lib/worker';

function* ensureAllChannel() {
  const isThreadLoaded = yield select(messagesLoadedSelector);
  if (!isThreadLoaded) {
    try {
      const threads = channelsToThreads(CONSTANTS.BUILD_ENV, yield call(sbChannelList, 100));
      yield put({
        type: LegacyActions.chatGetThreadsAction.success,
        payload: threads,
      });
    } catch (err) {
      const isThreadLoaded = yield select(messagesLoadedSelector);
      if (!isThreadLoaded) {
        notify.captureEvent(err);
      }
    }
  }
}

let isInited = false;

const stopWorker = (worker) => () => {
  worker.terminate('getChatMessages');
  isInited = false;
};

export default function* chat(dispatch) {
  // Wait for org features to load before checking the flag — saga is forked at
  // app startup before features are available.
  yield take(getOrganizationFeatures.success);

  // Skip entire legacy saga in v4 mode — shared actions like setActiveChannelAction
  // are dispatched in v4 mode too, and v3 handlers would crash without a Sendbird instance.
  const isNewChat = yield select(isNewChatSelector);
  if (isNewChat) return;

  yield takeEvery(LegacyActions.chatConnectAction.success, ensureAllChannel);
  const appId = getRuntimeConfig().env.REACT_APP_SENDBIRD_APP_ID_LEGACY;

  if (appId) {
    yield takeEvery(LegacyActions.sendMessageAction.type, Chat.ensureSendMessage);
    yield takeEvery(LegacyActions.receiveMessageAction.success, Chat.receiveMessage);

    yield takeEvery(setActiveChannelAction.type, Chat.ensureActiveChannel);
    yield takeLatest(LegacyActions.getPreviousMessageAction.type, Chat.ensureGetPreviousMessage);
  }
  const MW = new MultyWorker(dispatch, { withCompany: false });

  yield takeEvery(signOutAction.type, stopWorker(MW));

  yield takeEvery([setOpenAction.type, LegacyActions.setAllowedChatWorkerAction.type], function* fn() {
    const isAllowed = yield select(allowedChatWorkerSelector);
    if (!isAllowed) {
      MW.pause('getChatMessages');
    } else {
      if (!isInited) {
        yield call(MW.init, ensureAllChatChannel);
        isInited = true;
      }
      MW.resume('getChatMessages');
    }
  });
  yield call(MW.listen);
}
